import { produce } from "immer";

const industries = [
  { id: "i-0", items: [{ id: 0, label: "Automotive" }] },

  { id: "i-1", items: [{ id: 1, label: "Consumer Packaged Goods" }] },

  {
    id: "i-2",
    label: "Education",
    items: [
      { id: 2, label: "Education general" },
      { id: 3, label: "Higher Ed" },
      { id: 4, label: "K - 12" },
    ],
  },

  {
    id: "i-3",
    label: "Financial Services",
    items: [
      { id: 5, label: "Financial Services general" },
      { id: 6, label: "Insurance" },
      { id: 7, label: "Retail Banking" },
      { id: 8, label: "Wealth Management" },
    ],
  },

  {
    id: "i-4",
    label: "Health and Life Sciences",
    items: [
      { id: 9, label: "Health and Life Sciences general" },
      { id: 10, label: "Payer" },
      { id: 11, label: "Pharmaceutical" },
      { id: 12, label: "Provider" },
      { id: 13, label: "Medical Devices" },
    ],
  },

  {
    id: "i-5",
    label: "High Tech",
    items: [
      { id: 14, label: "High Tech general" },
      { id: 15, label: "B2B" },
      { id: 16, label: "B2C" },
      { id: 17, label: "Manufacturing & Distribution general" },
    ],
  },

  {
    id: "i-6",
    label: "Media, Entertainment & Communications",
    items: [
      { id: 18, label: "Media, Entertainment & Communications general" },
      { id: 19, label: "Digital Publishing" },
      { id: 20, label: "Paid TV & D2C Video" },
      { id: 21, label: "Sports, Gaming, & Gambling" },
      { id: 22, label: "Telecommunications" },
      { id: 23, label: "Non-Profit general" },
    ],
  },

  {
    id: "i-7",
    label: "Public Sector",
    items: [
      { id: 24, label: "Public Sector general" },
      { id: 25, label: "Department of Defense" },
      { id: 26, label: "Federal and Civilian" },
      { id: 27, label: "Intel" },
      { id: 28, label: "State and Local" },
    ],
  },

  {
    id: "i-8",
    label: "Retail",
    items: [{ id: 29, label: "Retail general" }],
  },

  {
    id: "i-9",
    label: "Travel & Hospitality",
    items: [
      { id: 30, label: "Travel & Hospitality general" },
      { id: 31, label: "Fitness" },
      { id: 32, label: "Hospitality" },
      { id: 33, label: "Restaurant" },
      { id: 34, label: "Transportation" },
    ],
  },
];

const sampleData = {
  companyName: "CitiSignal",
  industry: "Telecommunications",
  companyDescription:
    "E.g. has a wide range of teleco offerings, including carrier plans, phone devices, 5G mobile network, streaming TV. It's focused mostly on young, tech-savvy audience.",
  personaName: "Alex",
  personaDescription:
    "E.g. 30 years old woman, with 2 kids, who lives in San Jose, and is looking to optimize costs of her mobile provider. She currently has a basic 'On Demand' plan with CitiSignal.",
  businessGoals: "E.g. increase the upsell and build loyalty",
  featuredOfferings:
    "E.g. Premium' plan, streaming TV plan with special Kids channels, discount for event tickets by Townsend Events for mobile app users",
  featuredPhases: "E.g. onboarding and loyalty",
  featuredUseCases:
    "E.g. retargeting of the abandoner, inbound and outbound marketing channels, in-store experiences",
  featuredEvents:
    "E.g. interacting with chat and call center and visiting a CitiSignal store",
};

const MAX_STEP = 3;

const initialState = {
  wizard: {
    step: 0,
    storyPrompt: "",
    storyPromptTemplate: "",
    data: {
      businessGoals: "",
      companyDescription: "",
      companyName: "",
      featuredEvents: "",
      featuredOfferings: "",
      featuredUseCases: "",
      industry: "",
      personas: [],
    },
    selectedIndustryKey: null,
    industries,
    sampleData,
  },
};

export const createWizardSlice = (set, get) => ({
  ...initialState,
  /**
   * Sets a value in the wizard data.
   *
   * @param {string} key - The key of the data to set.
   * @param {*} value - The value to set.
   *
   * @returns {void}
   */
  setData: (key, value) => {
    set(
      produce((state) => {
        state.wizard.data[key] = value;
      }),
      undefined,
      "wizard/setData",
    );

    get().setStoryPrompt();
  },
  /**
   * Adds a new persona to the wizard data.
   *
   * @returns {void}
   */
  addDataPersona: () =>
    set(
      produce((state) => {
        state.wizard.data.personas.push({});
      }),
      undefined,
      "wizard/addDataPersona",
    ),
  /**
   * Removes a persona from the wizard data.
   *
   * @param {number} index - The index of the persona to remove.
   *
   * @returns {void}
   */
  removeDataPersona: (index) => {
    set(
      produce((state) => {
        state.wizard.data.personas.splice(index, 1);
      }),
      undefined,
      "wizard/removeDataPersona",
    );
    get().setStoryPrompt();
  },
  /**
   * Updates a persona in the wizard data.
   *
   * @param {number} index - The index of the persona to update.
   * @param {string} key - The key of the persona data to update.
   * @param {*} value - The new value for the persona data.
   *
   * @returns {void}
   */
  changeDataPersona: (index, key, value) => {
    set(
      produce((state) => {
        state.wizard.data.personas[index][key] = value;
      }),
      undefined,
      "wizard/changeDataPersona",
    );
    get().setStoryPrompt();
  },
  /**
   * Changes the industry in the wizard data.
   *
   * @param {string} key - The key of the industry to change.
   *
   * @returns {void}
   */
  changeDataIndustry: (key) => {
    set(
      produce((state) => {
        const industry = state.wizard.industries
          .flatMap((section) => section.items)
          .find((item) => item.id === key);

        if (!industry) {
          console.warn(`Industry with key ${key} not found`);
          return;
        }

        state.wizard.data.industry = industry.label;
        state.wizard.selectedIndustryKey = key;

        if (state.wizard.step === 0) {
          state.wizard.step = 1; // Move to the next step if on the first step
        }
      }),
      undefined,
      "wizard/changeDataIndustry",
    );
    get().setStoryPrompt();
  },
  /**
   * Sets the wizard step.
   *
   * @param {number} step - The step to set.
   *
   * @returns {void}
   */
  setWizardStep: (step) =>
    set(
      produce((state) => {
        state.wizard.step = step;
      }),
      undefined,
      "wizard/setWizardStep",
    ),
  /**
   * Builds the story prompt based on the current wizard data.
   *
   * @returns {string} - The story prompt based on the current wizard data.
   */
  buildStoryPrompt: () => {
    const {
      industry,
      companyName,
      companyDescription,
      personas: _personas = [],
      businessGoals,
      featuredOfferings,
      featuredPhases,
      featuredUseCases,
      featuredEvents,
    } = get().wizard.data;

    const personas = _personas.filter((persona) => persona.name);

    if (!industry || !companyName) {
      return "";
    }

    const baseInstruction = `Write a story, that will help explain the CEO of the ${industry} company ${!!companyName && `called ${companyName}`} what the modern marketing technology can do to power the end-to-end customer journey and achieve their business goals${!!businessGoals ? ` (especially ${businessGoals}` : ""}, while optimizing costs and increasing revenue.`;

    const personasDescription = !personas.length
      ? ""
      : personas.length === 1
        ? `\n\nThe story's hero, our *persona*, is ${personas[0].name} – ${personas[0].description}.`
        : `\n\nThe story's heroes, our *personas*, are:
    ${personas.map((persona) => `- ${persona.name}${!!persona.description ? ` – ${persona.description}` : ""}`).join("\n")}.`;

    const personasNames = personas.length
      ? new Intl.ListFormat("en", {
          style: "long",
          type: "conjunction",
        }).format(personas.map((persona) => persona.name))
      : `our persona`;

    const task = `\n\nThe story should show the experience of ${personasNames}, encompassing the whole customer journey, from the acquisition to retention, that is powered by modern digital experience solutions.
    ${!!featuredPhases ? `\nSpecial focus should be put on the ${featuredPhases} phases of the customer journey.` : ""}
    Experiences should include the key industry use cases, and show personalized digital experiences, real-time customer profile use, orchestration of user journey across channels${!!featuredUseCases ? `\n as well as ${featuredUseCases}.` : "."} \
    ${!!featuredEvents ? `\nPart of the story should feature the following events: ${featuredEvents}.` : ""}`;

    const company = `${!!companyDescription ? `\n\n${companyName} – ${companyDescription}` : ""} \
    ${!!featuredOfferings ? `\n${companyName}'s offerings that the story should show include ${featuredOfferings}` : ""}`;

    const prompt = `${baseInstruction} \
    ${personasDescription} \
    ${task} \
    ${company}`;

    return prompt;
  },
  /**
   * Builds the story prompt template based on the current wizard data.
   *
   * @returns {string} - The story prompt template based on the current wizard data.
   */
  buildStoryPromptTemplate: () => {
    const storyPrompt = get().wizard.storyPrompt;
    const industry = get().wizard.data.industry;

    if (!storyPrompt || !industry) {
      return "";
    }

    return `${storyPrompt}
    
    Build the story narrative in a way that following steps are connected to each other, and the whole story is cohesive, logical, and engaging.
    Connect each step of the story with a realistic (real-world events) narrative, by creating additional, intermediate steps, and transitioning smoothly between them. Make sure that each step is connected to the previous one.
    The story should be written in a way that it can be presented as a script for a presentation, with each step being a separate slide.
    
    Include those steps at the beginning of the story script:
      - "Intro" step with only title "Adobe × *companyName*"
      - "Cover" step with the headline
      - general "Introduction" to the story (in 1-4 steps)
      - "Introduction" step for each persona
    
    Include those steps at the end of the story script:
      - "Summary" for used Adobe solutions. Summarize the solutions used in the story, and how they help to achieve the business goals.
      - "Summary" for business values. Summarize the business values achieved in the story.
      - "Outro" step with only the title "Adobe"
    
    
    The output should follow exactly the JSON format of provided examples, tell the story in similar way, and follow the overall idiolect and sociolinguistics style used in the example scripts.
    The output JSON should include the following fields:
      *title* – A title is a name assigned to a work, typically selected by its creator. It serves to identify the work, provide context, offer a brief summary of its content, and capture the reader's interest.
      *customer* – The company highlighted in this story is defined as a customer, which refers to a business or organization that buys goods or services from another business in return for payment.
      *industry* – A collection of companies that engage in similar activities and provide related products or services.
      *storyOverview* – A story overview provides a general summary of a story, highlighting the main events and plot points while omitting minor details. It's a concise way to understand the core narrative arc without getting bogged down in specific instances.
      *businessGoals* – The goals or objectives a company seeks to accomplish within a designated time period.
      *campaignFocus* - The central theme, problem, or result that directs all campaign activities and communications.
      *personas*: – A persona represents a fictional member of a target audience, utilized in fields such as marketing and design to enhance understanding and empathy towards users. It is developed through research and insights regarding users, including their goals, motivations, and behaviors, informing design choices and leading to the creation of more effective products and experiences.
        - *name* – Name of the persona.
        - *role* – Persona's role in the story.
        - *description* – Short overview of the persona.
        - *location* – City where the persona lives.
        - *age* – Age of persona.
      *solutions* – List of Adobe solutions used across the story.
      *script*: – List of the steps in the story script.
        - *section* – (Required) The title of the ongoing chapter in the narrative.
        - *title* – (Required) A title or label that indicates the current phase.
        - *text* – (Required) A brief message outlining the current step. It should be formatted suitably for a presentation slide.
        - *personas* – List of persona names described or play a role in this step. Required if the step is related to or talk about a specific persona.
        - *solutions* – The titles of the Adobe solutions utilized in this phase.
        - *visual* – (Required) Prompt for generating a visual image related to the step.
        - *visualMessaging* – Messaging that corresponds with the visual shown.
        - *comments* – (Required) Explaining the rationale for this stage of the story script, discussing the values given, and concentrating on reaching business objectives.
    `;
  },
  /**
   * Sets the story prompt in the wizard state.
   *
   * @param {string} prompt - The story prompt to set.
   *
   * @returns {void}
   */
  setStoryPrompt: (prompt) => {
    set(
      produce((state) => {
        state.wizard.storyPrompt = prompt ?? get().buildStoryPrompt();
      }),
      undefined,
      "wizard/setStoryPrompt",
    );
    get().setStoryPromptTemplate();
  },
  /**
   * Sets the story prompt template in the wizard state.
   *
   * @param {string} template - The story prompt template to set.
   *
   * @returns {void}
   */
  setStoryPromptTemplate: (template) =>
    set(
      produce((state) => {
        state.wizard.storyPromptTemplate =
          template ?? get().buildStoryPromptTemplate();
      }),
      undefined,
      "wizard/setStoryPromptTemplate",
    ),
  /**
   * Loads the wizard data.
   *
   * @param {Object} data - The wizard data to load.
   * @param {string} prompt - The story prompt to set.
   * @param {string} template - The story prompt template to set.
   *
   * @returns {void}
   */
  loadWizardData: (data, prompt, template) => {
    set(
      produce((state) => {
        state.wizard.data = data;
        state.wizard.storyPrompt = prompt;
        state.wizard.storyPromptTemplate = template;
        state.wizard.step = MAX_STEP;

        const industry = state.wizard.industries
          .flatMap((section) => section.items)
          .find((item) => item.label === data.industry);

        if (industry) {
          state.wizard.selectedIndustryKey = industry.id;
        }
      }),
      undefined,
      "wizard/loadWizardData",
    );
    get().changeDataIndustry(data.industry);
  },
  /**
   * Resets the wizard state to its initial values.
   *
   * @returns {void}
   */
  resetWizard: () => set({ ...initialState }, undefined, "wizard/resetWizard"),
});
